import React, { useEffect } from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation, Outlet, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { CompareProvider } from "@/contexts/CompareContext";
import { ShortlistProvider } from "@/contexts/ShortlistContext";
import PropertyRegistrationWizard from "@/pages/dashboard/PropertyRegistrationWizard";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CompareDrawer from "@/components/CompareDrawer";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";

import Home from "@/pages/Home";
import Properties from "@/pages/Properties";
import PropertyDetail from "@/pages/PropertyDetail";
import Compare from "@/pages/Compare";
import Recommendations from "@/pages/Recommendations";
import PlotOnDemand from "@/pages/PlotOnDemand";
import Agents from "@/pages/Agents";
import AgentProfile from "@/pages/AgentProfile";
import Offices from "@/pages/Offices";
import OfficeProfile from "@/pages/OfficeProfile";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Terms from "@/pages/Terms";
import FAQ from "@/pages/FAQ";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import EMICalculator from "@/pages/EMICalculator";
import Plots from "@/pages/Plots";
import CategoryLanding from "@/pages/CategoryLanding";
import ForgotPassword from "@/pages/ForgotPassword";
import RecentSearches from "@/pages/RecentSearches";
import NotFound from "@/pages/NotFound";

import DashboardLayout from "@/components/DashboardLayout";
import Overview from "@/pages/dashboard/Overview";
import MyProperties from "@/pages/dashboard/MyProperties";
import Appointments from "@/pages/dashboard/Appointments";
import AdminApprovals from "@/pages/dashboard/AdminApprovals";
import AdminUsers from "@/pages/dashboard/AdminUsers";
import CityListings from "@/pages/CityListings";
import AdminLeadAssignments from "@/pages/dashboard/AdminLeadAssignments";
import AgentMyVisits from "@/pages/dashboard/AgentMyVisits";
import OfficeVisitsQueue from "@/pages/dashboard/OfficeVisitsQueue";
import OfficeCrmLeads from "@/pages/dashboard/OfficeCrmLeads";
import SavedProperties from "@/pages/dashboard/SavedProperties";
import PlotDemandsDashboard from "@/pages/dashboard/PlotDemandsDashboard";
import Leads from "@/pages/dashboard/Leads";
import Notifications from "@/pages/dashboard/Notifications";
import TrendingLeaderboard from "@/pages/dashboard/TrendingLeaderboard";
import OfficeVerification from "@/pages/dashboard/OfficeVerification";
import AdminAnalytics from "@/pages/dashboard/AdminAnalytics";
import AdminFaqs from "@/pages/dashboard/AdminFaqs";
import AdminReviews from "@/pages/dashboard/AdminReviews";
import AdminOperations from "@/pages/dashboard/AdminOperations";
import AdminLeadExplorer from "@/pages/dashboard/AdminLeadExplorer";
import AdminSiteVisitWorkbench from "@/pages/dashboard/AdminSiteVisitWorkbench";
import AdminSiteVisitAssignments from "@/pages/dashboard/AdminSiteVisitAssignments";
import AdminPlotDemandWorkbench from "@/pages/dashboard/AdminPlotDemandWorkbench";
import AdminWorkflowOverview from "@/pages/dashboard/AdminWorkflowOverview";
import AdminPeopleWorkspace from "@/pages/dashboard/AdminPeopleWorkspace";
import AdminPropertyExplorer from "@/pages/dashboard/AdminPropertyExplorer";
import UpdateProfile from "@/pages/dashboard/UpdateProfile";
import Enquiries from "@/pages/dashboard/Enquiries";
import { PHASE1_ENABLED } from "@/lib/featureFlags";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AppRouter() {
  return (
    <>
      <ScrollToTop />
      <ErrorBoundary>
      <Routes>
        <Route element={<PublicLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/properties" element={<Properties />} />
          <Route path="/properties/:id" element={<PropertyDetail />} />
          <Route path="/compare" element={<Compare />} />
          <Route path="/recommendations" element={<Recommendations />} />
          <Route path="/plot-on-demand" element={
            <ProtectedRoute roles={["buyer", "owner"]}><PlotOnDemand /></ProtectedRoute>
          } />
          <Route path="/plots" element={<Plots />} />
          <Route path="/land-for-sale-in-:city" element={<CityListings defaultCategory="LAND" />} />
          <Route path="/plots-for-sale-in-:city" element={<CityListings defaultCategory="PLOT" />} />
          <Route path="/buy" element={<CategoryLanding type="buy" />} />
          <Route path="/rent" element={<CategoryLanding type="rent" />} />
          <Route path="/commercial" element={<CategoryLanding type="commercial" />} />
          <Route path="/pg-hostel" element={<CategoryLanding type="pg" />} />
          <Route path="/plot-land" element={<CategoryLanding type="plot" />} />
          <Route path="/agents" element={<Agents />} />
          <Route path="/agents/:id" element={<AgentProfile />} />
          <Route path="/offices" element={<Offices />} />
          <Route path="/offices/:id" element={<OfficeProfile />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/register" element={<Register />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/emi-calculator" element={<EMICalculator />} />
          <Route path="/recent-searches" element={<RecentSearches />} />
        </Route>

        <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
          <Route path="/dashboard" element={<Overview />} />
          <Route path="/dashboard/properties" element={<MyProperties />} />
          <Route path="/dashboard/properties/new" element={
            <ProtectedRoute roles={["agent", "owner", "office", "admin"]}><PropertyRegistrationWizard /></ProtectedRoute>
          } />
          <Route path="/dashboard/property/new" element={
            <ProtectedRoute roles={["owner", "agent", "office", "admin"]}><PropertyRegistrationWizard /></ProtectedRoute>
          } />
          <Route path="/dashboard/appointments" element={<Appointments />} />
          <Route path="/dashboard/saved" element={<SavedProperties />} />
          <Route path="/dashboard/plot-demands" element={<PlotDemandsDashboard />} />
          <Route path="/dashboard/profile" element={<UpdateProfile />} />
          <Route path="/dashboard/enquiries" element={<Enquiries />} />
          <Route path="/dashboard/admin/approvals" element={
            <ProtectedRoute roles={["admin", "super_admin"]}><AdminApprovals /></ProtectedRoute>
          } />
          <Route path="/dashboard/office/verification" element={
            <ProtectedRoute roles={["office"]}><OfficeVerification /></ProtectedRoute>
          } />
          <Route path="/dashboard/office/site-visits" element={
            <ProtectedRoute roles={["office", "admin"]}><OfficeVisitsQueue /></ProtectedRoute>
          } />
          <Route path="/dashboard/agent/visits" element={
            <ProtectedRoute roles={["agent", "admin", "super_admin"]}><AgentMyVisits /></ProtectedRoute>
          } />
          <Route path="/dashboard/office/crm-leads" element={
            <ProtectedRoute roles={["office", "admin"]}><OfficeCrmLeads /></ProtectedRoute>
          } />
          <Route path="/dashboard/admin/lead-assignments" element={
            <ProtectedRoute roles={["admin", "super_admin"]}><AdminLeadAssignments /></ProtectedRoute>
          } />
          <Route path="/dashboard/admin/operations" element={
            <Phase1FrontendRoute>
              <ProtectedRoute roles={["admin", "super_admin"]}><AdminOperations /></ProtectedRoute>
            </Phase1FrontendRoute>
          } />
          <Route path="/dashboard/admin/lead-explorer" element={
            <Phase1FrontendRoute>
              <ProtectedRoute roles={["admin", "super_admin"]}><AdminLeadExplorer /></ProtectedRoute>
            </Phase1FrontendRoute>
          } />
          <Route path="/dashboard/admin/site-visits" element={
            <ProtectedRoute roles={["admin", "super_admin"]}><AdminWorkflowOverview type="site-visits" /></ProtectedRoute>
          } />
          <Route path="/dashboard/admin/site-visits/workbench" element={
            <Phase1FrontendRoute>
              <ProtectedRoute roles={["admin", "super_admin"]}><AdminSiteVisitWorkbench /></ProtectedRoute>
            </Phase1FrontendRoute>
          } />
          <Route path="/dashboard/admin/site-visits/assignments" element={
            <ProtectedRoute roles={["admin", "super_admin"]}><AdminSiteVisitAssignments /></ProtectedRoute>
          } />
          <Route path="/dashboard/admin/analytics" element={
            <Phase1FrontendRoute>
              <ProtectedRoute roles={["admin", "super_admin"]}><AdminAnalytics /></ProtectedRoute>
            </Phase1FrontendRoute>
          } />
          <Route path="/dashboard/admin/users" element={
            <ProtectedRoute roles={["admin", "super_admin"]}><AdminPeopleWorkspace mode="users" /></ProtectedRoute>
          } />
          <Route path="/dashboard/admin/agents" element={
            <ProtectedRoute roles={["admin", "super_admin"]}><AdminPeopleWorkspace mode="agents" /></ProtectedRoute>
          } />
          <Route path="/dashboard/admin/offices" element={
            <ProtectedRoute roles={["admin", "super_admin"]}><AdminPeopleWorkspace mode="offices" /></ProtectedRoute>
          } />
          <Route path="/dashboard/admin/users/manage" element={
            <ProtectedRoute roles={["admin", "super_admin"]}><AdminUsers tab="users" /></ProtectedRoute>
          } />
          <Route path="/dashboard/admin/agents/manage" element={
            <ProtectedRoute roles={["admin", "super_admin"]}><AdminUsers tab="users" roleFilter="agent" /></ProtectedRoute>
          } />
          <Route path="/dashboard/admin/offices/manage" element={
            <ProtectedRoute roles={["admin", "super_admin"]}><AdminUsers tab="offices" /></ProtectedRoute>
          } />
          <Route path="/dashboard/admin/properties" element={
            <ProtectedRoute roles={["admin", "super_admin"]}><AdminPropertyExplorer /></ProtectedRoute>
          } />
          <Route path="/dashboard/admin/properties/manage" element={
            <ProtectedRoute roles={["admin", "super_admin"]}><AdminUsers tab="properties" /></ProtectedRoute>
          } />
          <Route path="/dashboard/admin/plots" element={
            <ProtectedRoute roles={["admin", "super_admin"]}><AdminPropertyExplorer scope="plots" /></ProtectedRoute>
          } />
          <Route path="/dashboard/admin/plots-on-demand" element={
            <ProtectedRoute roles={["admin", "super_admin"]}><AdminWorkflowOverview type="plots-on-demand" /></ProtectedRoute>
          } />
          <Route path="/dashboard/admin/plots-on-demand/workbench" element={
            <Phase1FrontendRoute>
              <ProtectedRoute roles={["admin", "super_admin"]}><AdminPlotDemandWorkbench /></ProtectedRoute>
            </Phase1FrontendRoute>
          } />
          <Route path="/dashboard/admin/plots-on-demand/assignments" element={
            <ProtectedRoute roles={["admin", "super_admin"]}><AdminPlotDemandWorkbench mode="assignments" /></ProtectedRoute>
          } />
          <Route path="/dashboard/admin/plots-on-demand/manage" element={
            <ProtectedRoute roles={["admin", "super_admin"]}><AdminUsers tab="plots" /></ProtectedRoute>
          } />
          <Route path="/dashboard/admin/plot-demands" element={
            <ProtectedRoute roles={["admin", "super_admin"]}><RedirectWithSearch to="/dashboard/admin/plots-on-demand" /></ProtectedRoute>
          } />
          <Route path="/dashboard/admin/faqs" element={
            <ProtectedRoute roles={["admin", "super_admin"]}><AdminFaqs /></ProtectedRoute>
          } />
          <Route path="/dashboard/admin/reviews" element={
            <ProtectedRoute roles={["admin", "super_admin"]}><AdminReviews /></ProtectedRoute>
          } />
          <Route path="/dashboard/trending" element={
            <ProtectedRoute roles={["admin", "super_admin"]}><TrendingLeaderboard /></ProtectedRoute>
          } />
          <Route path="/dashboard/leads" element={
            <ProtectedRoute roles={["agent", "office", "admin"]}><Leads /></ProtectedRoute>
          } />
          {/* Admin alias preserves the cross-entity deep-link contract while
              reusing the existing Lead Management implementation. */}
          <Route path="/dashboard/admin/leads" element={
            <ProtectedRoute roles={["admin", "super_admin"]}><Leads /></ProtectedRoute>
          } />
          <Route path="/dashboard/notifications" element={<Notifications />} />
        </Route>

        {/* Catch-all: unknown URLs render a friendly 404 instead of a blank screen. */}
        <Route path="*" element={<PublicLayout />}>
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
      </ErrorBoundary>
    </>
  );
}

function PublicLayout() {
  const { pathname } = useLocation();
  const isAuthRoute = ["/login", "/register", "/forgot-password"].includes(pathname);
  return (
    <div className="site-shell min-h-screen flex flex-col bg-background">
      {/* Shared ambient layer adds depth to every public page without changing its layout. */}
      <div className="premium-ambient" aria-hidden="true">
        <span />
        <span />
      </div>
      {!isAuthRoute && <Navbar />}
      <main className="premium-page-surface relative z-[1] flex-1">
        <Outlet />
      </main>
      {!isAuthRoute && <Footer />}
      {!isAuthRoute && <CompareDrawer />}
    </div>
  );
}

// Keeps old internal/admin bookmarks useful while moving to unambiguous names.
function RedirectWithSearch({ to }) {
  const { search } = useLocation();
  return <Navigate replace to={`${to}${search}`} />;
}

function Phase1FrontendRoute({ children }) {
  if (PHASE1_ENABLED) {
    return <Navigate replace to="/dashboard" />;
  }
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <CompareProvider>
        <ShortlistProvider>
          <BrowserRouter>
            <AppRouter />
            <Toaster position="top-right" />
          </BrowserRouter>
        </ShortlistProvider>
      </CompareProvider>
    </AuthProvider>
  );
}
